import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAddToCart } from '@/hooks/useAddToCart';
import { Spinner } from '@atoms/Spinner';
import {
  HeroSection,
  CategoryGrid,
  CampaignCarousel,
  BestSellerGrid,
  TopWholesalersGrid,
} from '@organisms/index';
import { TrustBar } from '@organisms/marketing/TrustBar';
import { ValuePropsSection } from '@organisms/marketing/ValuePropsSection';
import { HowItWorksSection } from '@organisms/marketing/HowItWorksSection';
import { MarketingCtaBand } from '@organisms/marketing/MarketingCtaBand';
import { CategoryPreviewModal } from '@organisms/CategoryPreviewModal';
import { marketingService } from '@services/marketingService';
import { scrollToSection } from '@/utils/scrollToSection';
import type { Category } from '@/types';

export function LandingPage() {
  const { requestAdd } = useAddToCart('/');
  const [previewCategory, setPreviewCategory] = useState<Category | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['landing'],
    queryFn: () => marketingService.getLandingData(),
  });

  useEffect(() => {
    if (isLoading) return;
    const hash = window.location.hash.slice(1);
    if (hash) {
      requestAnimationFrame(() => scrollToSection(hash));
    }
  }, [isLoading]);

  if (isLoading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="section-container py-12">
        <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-center text-red-700">
          Ana sayfa verileri yüklenemedi. API sunucusunun çalıştığından emin olun.
        </div>
      </div>
    );
  }

  return (
    <>
      <HeroSection ads={data?.heroAds ?? []} />
      {data && (
        <TrustBar
          data={{
            categories: data.categories,
            bestSellers: data.bestSellers,
            campaigns: data.campaigns,
            topWholesalers: data.topWholesalers,
          }}
        />
      )}
      <ValuePropsSection />
      <HowItWorksSection />
      <CategoryGrid categories={data?.categories ?? []} onSelect={setPreviewCategory} />
      <TopWholesalersGrid wholesalers={data?.topWholesalers ?? []} />
      <CampaignCarousel campaigns={data?.campaigns ?? []} />
      <BestSellerGrid products={data?.bestSellers ?? []} onAdd={requestAdd} />
      <MarketingCtaBand />
      <CategoryPreviewModal category={previewCategory} onClose={() => setPreviewCategory(null)} />
    </>
  );
}
